import React from 'react';
import {
  Box,
  Text,
  Loader,
  Alert,
  Tooltip,
  Center,
  Group,
  Paper
} from "@mantine/core";
import config from "../../../../config.js";
import useAuthenticatedData from "../../../../hooks/useAuthenticatedData";

const RepairsIndividualPerformancePanel = ({ selectedPerson }) => {
  const url = selectedPerson
    ? `${config.apiBaseUrl}/supply-chain/repairs/individual/scorecard?ccode=${selectedPerson.cCode}`
    : null;
  const { data: scorecardData, isLoading, error } = useAuthenticatedData(url);

  if (!selectedPerson) {
    return (
      <Paper p="lg" style={{ textAlign: "center", backgroundColor: "var(--mantine-color-dark-8)" }}>
        <Text size="lg" c="white">
          Please select a person to view their scorecard
        </Text>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        Error loading scorecard data: {error.message ?? error}
      </Alert>
    );
  }

  if (!scorecardData) {
    return <Alert color="blue" title="No Data">No scorecard data available for {selectedPerson.fullName}</Alert>;
  }

  // Map the status from the API to actual colors for background and border.
  const statusColorMapping = {
    green: { background: '#2A7040', border: '#1E5A33' },
    yellow: { background: '#A07D32', border: '#7C6025' },
    red: { background: '#944848', border: '#7A3636' },
    gray: { background: '#4F4F4F', border: '#3B3B3B' }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (isNaN(value)) return 'N/A';
    return `${Math.round(value)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getMarginPercentStatus = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'gray';
    if (value >= 20) return 'green';
    if (value >= 15) return 'yellow';
    return 'red';
  };

  const getInventoryHealthStatus = (value, ageBucket) => {
    const goals = {
      '0-90': 56,
      '91-180': 26,
      '181-365': 16,
      '366+': 2
    };

    const goal = goals[ageBucket];

    if (ageBucket === '0-90') {
      if (value >= goal) return 'green';
      if (value >= goal * 0.85) return 'yellow';
      return 'red';
    } else {
      if (value <= goal) return 'green';
      if (value <= goal * 1.15) return 'yellow';
      return 'red';
    }
  };

  // Helper functions for handling clicks with cCode
  const handleInventoryPlannedSpendClick = (period) => {
    window.open(`/supply-chain/repairs/${selectedPerson.cCode}/scorecard/inventory-planned-spend-details?period=${period}`, '_blank');
  };

  const handleInventoryLandedSpendClick = (period) => {
    window.open(`/supply-chain/repairs/${selectedPerson.cCode}/scorecard/inventory-landed-spend-details?period=${period}`, '_blank');
  };

  const handleInventoryRevenueClick = (period) => {
    window.open(`/supply-chain/repairs/${selectedPerson.cCode}/scorecard/inventory-revenue-details?period=${period}`, '_blank');
  };

  const handleInventoryHealthClick = (period) => {
    window.open(`/supply-chain/repairs/${selectedPerson.cCode}/scorecard/inventory-health-details?period=${period}`, '_blank');
  };

  const handleNewVendorROClick = (period) => {
    window.open(`/supply-chain/repairs/${selectedPerson.cCode}/scorecard/new-vendor-ro-details?period=${period}`, '_blank');
  };

  const handleNewVendorRFQClick = (period) => {
    window.open(`/supply-chain/repairs/${selectedPerson.cCode}/scorecard/new-vendor-rfq-details?period=${period}`, '_blank');
  };

  const CompactMetricBox = ({ value, format, status, onClick, tooltip }) => {
    const safeValue = value ?? 0;
    const hasData = safeValue !== null && !isNaN(safeValue);
    const { background, border } = statusColorMapping[status] || { background: '#4F4F4F', border: '#3B3B3B' };

    const formattedValue =
      format === 'currency'
        ? formatCurrency(hasData ? safeValue : 0)
        : format === 'percent'
          ? formatPercent(hasData ? safeValue : 0)
          : hasData
            ? format === 'number'
              ? formatNumber(safeValue)
              : safeValue.toFixed(1)
            : 'N/A';

    const boxContent = (
      <Box
        onClick={onClick}
        style={{
          backgroundColor: background,
          padding: '6px',
          borderRadius: '4px',
          border: `2px solid ${border}`,
          width: '120px',
          height: '32px',
          textAlign: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
        <Text 
          size="sm" 
          fw={600} 
          c="white"
          style={{ fontSize: '0.85rem' }}
        >
          {formattedValue}
        </Text>
      </Box>
    );

    return tooltip ? (
      <Tooltip label={tooltip} withArrow>
        {boxContent}
      </Tooltip>
    ) : boxContent;
  };

  const metrics = [
    {
      title: 'Planned Repairs Spend',
      goalDetails: ['Goal', 'Margin %: 23.4%'],
      rows: [
        {
          label: 'Spend',
          boxes: [
            {
              title: 'Month to Date',
              value: scorecardData.inventoryPlannedSpendMTD,
              status: 'gray',
              format: 'currency',
              mode: 'MTD'
            },
            {
              title: 'Qtr to Date',
              value: scorecardData.inventoryPlannedSpendQTD,
              status: 'gray',
              format: 'currency',
              mode: 'QTD'
            },
            {
              title: 'Year to Date',
              value: scorecardData.inventoryPlannedSpendYTD,
              status: 'gray',
              format: 'currency',
              mode: 'YTD'
            }
          ]
        },
        {
          label: 'Projected Margin %',
          boxes: [
            {
              title: 'Month to Date',
              value: scorecardData.inventoryPlannedSpendProjectedMarginPercentMTD,
              status: getMarginPercentStatus(scorecardData.inventoryPlannedSpendProjectedMarginPercentMTD),
              format: 'percent',
              mode: 'MTD'
            },
            {
              title: 'Qtr to Date',
              value: scorecardData.inventoryPlannedSpendProjectedMarginPercentQTD,
              status: getMarginPercentStatus(scorecardData.inventoryPlannedSpendProjectedMarginPercentQTD),
              format: 'percent',
              mode: 'QTD'
            },
            {
              title: 'Year to Date',
              value: scorecardData.inventoryPlannedSpendProjectedMarginPercentYTD,
              status: getMarginPercentStatus(scorecardData.inventoryPlannedSpendProjectedMarginPercentYTD),
              format: 'percent',
              mode: 'YTD'
            }
          ]
        },
        {
          label: 'Projected Margin $',
          boxes: [
            {
              title: 'Month to Date',
              value: scorecardData.inventoryPlannedSpendProjectedMarginDollarsMTD,
              status: 'gray',
              format: 'currency',
              mode: 'MTD'
            },
            {
              title: 'Qtr to Date',
              value: scorecardData.inventoryPlannedSpendProjectedMarginDollarsQTD,
              status: 'gray',
              format: 'currency',
              mode: 'QTD'
            },
            {
              title: 'Year to Date',
              value: scorecardData.inventoryPlannedSpendProjectedMarginDollarsYTD,
              status: 'gray',
              format: 'currency',
              mode: 'YTD'
            }
          ]
        }
      ],
      landed: {
        title: 'Landed Repairs Spend',
        goalDetails: ['Goal', 'Margin %: 23.4%'],
        rows: [
          {
            label: 'Spend',
            boxes: [
              {
                title: 'Month to Date',
                value: scorecardData.inventoryLandedSpendMTD,
                status: 'gray',
                format: 'currency',
                mode: 'MTD'
              },
              {
                title: 'Qtr to Date',
                value: scorecardData.inventoryLandedSpendQTD,
                status: 'gray',
                format: 'currency',
                mode: 'QTD'
              },
              {
                title: 'Year to Date',
                value: scorecardData.inventoryLandedSpendYTD,
                status: 'gray',
                format: 'currency',
                mode: 'YTD'
              }
            ]
          },
          {
            label: 'Projected Margin %',
            boxes: [
              {
                title: 'Month to Date',
                value: scorecardData.inventoryLandedSpendProjectedMarginPercentMTD,
                status: getMarginPercentStatus(scorecardData.inventoryLandedSpendProjectedMarginPercentMTD),
                format: 'percent',
                mode: 'MTD'
              },
              {
                title: 'Qtr to Date',
                value: scorecardData.inventoryLandedSpendProjectedMarginPercentQTD,
                status: getMarginPercentStatus(scorecardData.inventoryLandedSpendProjectedMarginPercentQTD),
                format: 'percent',
                mode: 'QTD'
              },
              {
                title: 'Year to Date',
                value: scorecardData.inventoryLandedSpendProjectedMarginPercentYTD,
                status: getMarginPercentStatus(scorecardData.inventoryLandedSpendProjectedMarginPercentYTD),
                format: 'percent',
                mode: 'YTD'
              }
            ]
          },
          {
            label: 'Projected Margin $',
            boxes: [
              {
                title: 'Month to Date',
                value: scorecardData.inventoryLandedSpendProjectedMarginDollarsMTD,
                status: 'gray',
                format: 'currency',
                mode: 'MTD'
              },
              {
                title: 'Qtr to Date',
                value: scorecardData.inventoryLandedSpendProjectedMarginDollarsQTD,
                status: 'gray',
                format: 'currency',
                mode: 'QTD'
              },
              {
                title: 'Year to Date',
                value: scorecardData.inventoryLandedSpendProjectedMarginDollarsYTD,
                status: 'gray',
                format: 'currency',
                mode: 'YTD'
              }
            ]
          }
        ]
      }
    },
    {
      title: 'Revenue/Margin',
      goalDetails: ['Goal', 'Margin %: 23.4%'],
      rows: [
        {
          label: 'Revenue',
          boxes: [
            {
              title: 'Month to Date',
              value: scorecardData.revenueMTD,
              status: 'gray',
              format: 'currency',
              mode: 'MTD'
            },
            {
              title: 'Qtr to Date',
              value: scorecardData.revenueQTD,
              status: 'gray',
              format: 'currency',
              mode: 'QTD'
            },
            {
              title: 'Year to Date',
              value: scorecardData.revenueYTD,
              status: 'gray',
              format: 'currency',
              mode: 'YTD'
            }
          ]
        },
        {
          label: 'Gross Margin %',
          boxes: [
            {
              title: 'Month to Date',
              value: scorecardData.revenueGrossMarginPercentMTD,
              status: getMarginPercentStatus(scorecardData.revenueGrossMarginPercentMTD),
              format: 'percent',
              mode: 'MTD'
            },
            {
              title: 'Qtr to Date',
              value: scorecardData.revenueGrossMarginPercentQTD,
              status: getMarginPercentStatus(scorecardData.revenueGrossMarginPercentQTD),
              format: 'percent',
              mode: 'QTD'
            },
            {
              title: 'Year to Date',
              value: scorecardData.revenueGrossMarginPercentYTD,
              status: getMarginPercentStatus(scorecardData.revenueGrossMarginPercentYTD),
              format: 'percent',
              mode: 'YTD'
            }
          ]
        },
        {
          label: 'Gross Margin $',
          boxes: [
            {
              title: 'Month to Date',
              value: scorecardData.revenueGrossMarginDollarsMTD,
              status: 'gray',
              format: 'currency',
              mode: 'MTD'
            },
            {
              title: 'Qtr to Date',
              value: scorecardData.revenueGrossMarginDollarsQTD,
              status: 'gray',
              format: 'currency',
              mode: 'QTD'
            },
            {
              title: 'Year to Date',
              value: scorecardData.revenueGrossMarginDollarsYTD,
              status: 'gray',
              format: 'currency',
              mode: 'YTD'
            }
          ]
        }
      ]
    },
    {
      title: 'Inventory Health',
      goalDetails: [
        '0-90 56%',
        '91-180 26%',
        '181-365 16%',
        '366+ 2%'
      ],
      rows: [
        {
          label: '0-90',
          boxes: [
            {
              title: 'Current',
              value: scorecardData.inventoryHealthPercent90,
              status: getInventoryHealthStatus(scorecardData.inventoryHealthPercent90, '0-90'),
              format: 'percent'
            }
          ]
        },
        {
          label: '91-180',
          boxes: [
            {
              title: 'Current',
              value: scorecardData.inventoryHealthPercent180,
              status: getInventoryHealthStatus(scorecardData.inventoryHealthPercent180, '91-180'),
              format: 'percent'
            }
          ]
        },
        {
          label: '181-365',
          boxes: [
            {
              title: 'Current',
              value: scorecardData.inventoryHealthPercent365,
              status: getInventoryHealthStatus(scorecardData.inventoryHealthPercent365, '181-365'),
              format: 'percent'
            }
          ]
        },
        {
          label: '366+',
          boxes: [
            {
              title: 'Current',
              value: scorecardData.inventoryHealthPercent365Plus,
              status: getInventoryHealthStatus(scorecardData.inventoryHealthPercent365Plus, '366+'),
              format: 'percent'
            }
          ]
        }
      ]
    }
  ];

  return (
    <Box style={{ padding: '4px', borderRadius: '8px', width: '100%', position: 'relative' }}>
      {metrics.map((metric, metricIndex) => (
        <Box
          key={metric.title}
          style={{
            marginBottom: '8px',
            padding: '6px',
            borderRadius: '4px',
            border: '1px solid var(--mantine-color-gray-3)',
            position: 'relative'
          }}
        >
          {metricIndex === 0 ? (
            <Box style={{ display: 'flex' }}>
              {/* Left side - Planned Repairs */}
              <Box style={{ width: '50%' }}>
                <Box style={{ display: 'flex', marginBottom: '6px' }}>
                  <Box style={{ width: '150px' }}>
                    <Text size="md" fw={500}>
                      {metric.title}
                    </Text>
                    {metric.goalDetails.map((detail, index) => (
                      <Text key={index} size="xs" c="dimmed" style={{ display: 'block' }}>
                        {detail}
                      </Text>
                    ))}
                  </Box>

                  <Box style={{ flex: 1 }}>
                    {metric.rows.map((row, rowIndex) => (
                      <Box
                        key={rowIndex}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '140px 120px 120px 120px',
                          gap: '6px',
                          marginBottom: '4px',
                          alignItems: 'center'
                        }}
                      >
                        <Text size="sm" fw={500} style={{ textAlign: 'right', paddingRight: '6px' }}>
                          {row.label}
                        </Text>
                        {row.boxes.map((box, index) => (
                          <CompactMetricBox
                            key={index}
                            value={box.value}
                            status={box.status}
                            format={box.format}
                            tooltip={box.tooltip}
                            onClick={box.mode ? () => handleInventoryPlannedSpendClick(box.mode) : undefined}
                          />
                        ))}
                      </Box>
                    ))}

                    <Box
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '140px 120px 120px 120px',
                        gap: '6px',
                        marginTop: '2px'
                      }}
                    >
                      <Box />
                      <Text size="xs" c="dimmed" ta="center">Month to Date</Text>
                      <Text size="xs" c="dimmed" ta="center">Qtr to Date</Text>
                      <Text size="xs" c="dimmed" ta="center">Year to Date</Text>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Right side - Landed Repairs */}
              {metric.landed && (
                <Box style={{ width: '50%' }}>
                  <Box style={{ display: 'flex', marginBottom: '6px' }}>
                    <Box style={{ width: '150px' }}>
                      <Text size="md" fw={500}>
                        {metric.landed.title}
                      </Text>
                      {metric.landed.goalDetails.map((detail, index) => (
                        <Text key={index} size="xs" c="dimmed" style={{ display: 'block' }}>
                          {detail}
                        </Text>
                      ))}
                    </Box>

                    <Box style={{ flex: 1 }}>
                      {metric.landed.rows.map((row, rowIndex) => (
                        <Box
                          key={rowIndex}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '140px 120px 120px 120px',
                            gap: '6px',
                            marginBottom: '4px',
                            alignItems: 'center'
                          }}
                        >
                          <Text size="sm" fw={500} style={{ textAlign: 'right', paddingRight: '6px' }}>
                            {row.label}
                          </Text>
                          {row.boxes.map((box, index) => (
                            <CompactMetricBox
                              key={index}
                              value={box.value}
                              status={box.status}
                              format={box.format}
                              onClick={box.mode ? () => handleInventoryLandedSpendClick(box.mode) : undefined}
                            />
                          ))}
                        </Box>
                      ))}

                      <Box
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '140px 120px 120px 120px',
                          gap: '6px',
                          marginTop: '2px'
                        }}
                      >
                        <Box />
                        <Text size="xs" c="dimmed" ta="center">Month to Date</Text>
                        <Text size="xs" c="dimmed" ta="center">Qtr to Date</Text>
                        <Text size="xs" c="dimmed" ta="center">Year to Date</Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            // Normal rendering for other metrics
            <Box style={{ display: 'flex', marginBottom: '6px' }}>
              <Box style={{ width: '150px' }}>
                <Text size="md" fw={500}>
                  {metric.title}
                </Text>
                {metric.goalDetails.map((detail, index) => (
                  <Text key={index} size="xs" c="dimmed" style={{ display: 'block' }}>
                    {detail}
                  </Text>
                ))}
              </Box>

              <Box style={{ flex: 1 }}>
                {metric.rows.map((row, rowIndex) => (
                  <Box
                    key={rowIndex}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 120px 120px 120px',
                      gap: '6px',
                      marginBottom: '4px',
                      alignItems: 'center'
                    }}
                  >
                    <Text size="sm" fw={500} style={{ textAlign: 'right', paddingRight: '6px' }}>
                      {row.label}
                    </Text>
                    {row.boxes.map((box, index) => (
                      <CompactMetricBox
                        key={index}
                        value={box.value}
                        status={box.status}
                        format={box.format}
                        onClick={
                          (metric.title === 'Revenue/Margin' && box.mode)
                            ? () => handleInventoryRevenueClick(box.mode)
                            : (metric.title === 'Inventory Health')
                              ? () => handleInventoryHealthClick('current')
                              : (metric.title === 'New Vendor Base Growth' && row.label === '# of RO Actual' && box.mode)
                                ? () => handleNewVendorROClick(box.mode)
                                : (metric.title === 'New Vendor Base Growth' && row.label === '# of RFQs' && box.mode)
                                  ? () => handleNewVendorRFQClick(box.mode)
                                  : undefined
                        }
                      />
                    ))}
                  </Box>
                ))}

                {metric.rows && metric.rows.length > 0 && metric.rows[0].boxes && metric.rows[0].boxes.length > 1 && (
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 120px 120px 120px',
                      gap: '6px',
                      marginTop: '2px'
                    }}
                  >
                    <Box />
                    <Text size="xs" c="dimmed" ta="center">Month to Date</Text>
                    <Text size="xs" c="dimmed" ta="center">Qtr to Date</Text>
                    <Text size="xs" c="dimmed" ta="center">Year to Date</Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default RepairsIndividualPerformancePanel;